import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import FrameworkAttributionChip from '@/components/ui/FrameworkAttributionChip/FrameworkAttributionChip.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import StepFormActions from '@/components/shared/StepFormActions/StepFormActions.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import {
  GovernedRecommendationSections,
  GovernedRecommendationSummary,
} from '@/pages/recommendationEngine/GovernedRecommendationSections.tsx';
import type {
  FrameworkLogicMatch,
  RecommendationEngineData,
  RecommendationEngineSteps,
} from '@/pages/recommendationEngine/recommendationEngineContext.ts';
import { cn } from '@/utils/classNames.ts';

/**
 * Step 3 — Governance review (Figma): same recommendation body as step 1,
 * plus Framework Attribution Chip and framework-logic confirmation before COMPASS.
 */
const StepGovernanceReview: FC = () => {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<
    RecommendationEngineSteps,
    RecommendationEngineData
  >();
  const d = contextData as Partial<RecommendationEngineData> | null;
  const recommendation = d?.recommendation;

  if (!recommendation) {
    return (
      <BrandCardSideContent>
        <Alert status="error">{t('recommendationEnginePage.errors.previewFailed')}</Alert>
      </BrandCardSideContent>
    );
  }

  const frameworkAttribution = d?.framework_attribution ?? recommendation.framework_id;
  const frameworkLogicMatch = d?.framework_logic_match;
  const canAccept = frameworkLogicMatch === 'yes';

  const setFrameworkMatch = (value: FrameworkLogicMatch) => {
    setContextData((prev) => ({ ...prev, framework_logic_match: value }));
  };

  return (
    <BrandCardSideContent>
      <div className="flex flex-col flex-1 gap-6 w-full min-h-0">
        <div className="flex flex-col gap-2 shrink-0">
          <StepFormTitle>{t('recommendationEnginePage.title')}</StepFormTitle>
          <StepFormSubtitle>{t('recommendationEnginePage.subtitle')}</StepFormSubtitle>
        </div>

        <div className="flex flex-col flex-1 gap-5 min-h-0 overflow-y-auto pr-0.5">
          <GovernedRecommendationSummary recommendation={recommendation} />

          <FrameworkAttributionChip frameworkId={frameworkAttribution} />

          <GovernedRecommendationSections recommendation={recommendation} />

          <div className="flex flex-col gap-3 pt-1 shrink-0">
            <p className="font-dm text-sm font-semibold text-black/85 text-center">
              {t('recommendationEnginePage.governanceReview.frameworkMatchQuestion')}
            </p>
            <div className="flex flex-row gap-3">
              <FrameworkMatchButton
                label={t('recommendationEnginePage.governanceReview.no')}
                selected={frameworkLogicMatch === 'no'}
                variant="outline"
                onClick={() => setFrameworkMatch('no')}
              />
              <FrameworkMatchButton
                label={t('recommendationEnginePage.governanceReview.yes')}
                selected={frameworkLogicMatch === 'yes'}
                variant="primary"
                onClick={() => setFrameworkMatch('yes')}
              />
            </div>
            {frameworkLogicMatch === 'no' && (
              <Alert status="warning">
                {t('recommendationEnginePage.governanceReview.frameworkMismatchHint')}
              </Alert>
            )}
          </div>
        </div>

        <StepFormActions
          className="shrink-0"
          onBack={previousStep}
          submitType="button"
          onContinue={() => nextStep()}
          continueDisabled={!canAccept}
          continueLabel={t('recommendationEnginePage.ready.acceptContinue')}
        />
      </div>
    </BrandCardSideContent>
  );
};

export default StepGovernanceReview;

function FrameworkMatchButton({
  label,
  selected,
  variant,
  onClick,
}: {
  label: string;
  selected: boolean;
  variant: 'outline' | 'primary';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 py-3 px-4 rounded-xl font-dm font-bold text-sm tracking-wide transition-colors',
        variant === 'primary' && selected && 'bg-[#0F1B2A] text-white',
        variant === 'primary' && !selected && 'bg-white border border-[rgba(0,0,0,0.12)] text-[#0F1B2A]',
        variant === 'outline' && selected && 'bg-white border-2 border-[#0F1B2A] text-[#0F1B2A]',
        variant === 'outline' && !selected && 'bg-white border border-[rgba(0,0,0,0.12)] text-[#0F1B2A]',
      )}
    >
      {label}
    </button>
  );
}
